import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "San Diego", cities: "Mission Hills, North Park, Hillcrest, La Jolla, Pacific Beach" },
    { zone: "Chula Vista", cities: "Chula Vista, National City, Bonita, Otay Ranch" },
    { zone: "North County", cities: "Carlsbad, Oceanside, Encinitas, San Marcos, Escondido" },
    { zone: "East County", cities: "El Cajon, Santee, La Mesa, Spring Valley" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en San Diego</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo el condado de San Diego y sus alrededores.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en San Diego</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const SanDiegoSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "san-diego", name: "San Diego", type: "region", regionSlug: "san-diego" }}>
      <SEO
        title="Limpieza del Hogar en San Diego, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en San Diego, Chula Vista, Carlsbad, Oceanside y más. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar san diego, servicio de limpieza san diego ca, limpieza profunda san diego, empleada doméstica san diego, maid service san diego español, limpieza de casas chula vista, limpieza carlsbad oceanside encinitas"
        url="https://uplevelcleaning.com/es/san-diego"
        locationName="San Diego"
        regionSlug="san-diego"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "San Diego", url: "/es/san-diego" }
        ]}
        reviews={[
          { author: "Alejandra T.", rating: 5, reviewBody: "Vivo en Chula Vista y encontré Uplevel Cleaning en línea. ¡El mejor servicio de limpieza que he tenido! Puntuales, detallistas y con precios justos.", datePublished: "2026-03-22" },
          { author: "Miguel O.", rating: 5, reviewBody: "Contraté la limpieza profunda para mi casa en Carlsbad y quedé impresionado. El equipo fue muy profesional y la casa quedó impecable.", datePublished: "2026-02-17" },
          { author: "Rosa F.", rating: 5, reviewBody: "Con mi familia y mascota, la casa se ensucia rápido. Uplevel Cleaning viene cada dos semanas y es como tener una casa nueva. ¡Gracias!", datePublished: "2026-01-09" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="San Diego" regionSlug="san-diego" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en San Diego",
                "North County & South Bay",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="San Diego" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="san-diego" />
          <Guarantee />
          <SpanishFAQ cityName="San Diego" />
          <InternalLinks />
          <SpanishFinalCTA cityName="San Diego" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SanDiegoSpanishPage;
